import React from "react"
import {
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  Divider,
  Button,
  Chip,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Select,
  MenuItem,
  Collapse,
  Tooltip
} from "@mui/material"
import DownloadIcon from "@mui/icons-material/Download"
import EditIcon from "@mui/icons-material/Edit"
import RefreshIcon from "@mui/icons-material/Refresh"
import SearchIcon from "@mui/icons-material/Search"

import type { MeeshoCombinedReport } from "@/features/profit-calculators/calculator-shared/types"
import { useReportViewState } from "@/features/profit-calculators/calculator-shared/hooks/useReportViewState"
import {
  downloadOrderReport,
  downloadSkuReport,
  formatCurrency as fmt,
  getStatusColor
} from "@/features/profit-calculators/calculator-shared/utils/reportViewUtils"
import OrderBreakdownModal from "@/features/profit-calculators/calculator-shared/components/OrderBreakdownModal"

interface ReportViewProps {
  platform?: "meesho" | "amazon"
  report: MeeshoCombinedReport
  availableMonths?: string[]
  selectedMonth?: string
  onMonthChange?: (month: string) => void
  onEditPrices?: () => void
  onReset?: () => void
  readOnly?: boolean
}

const ReportView: React.FC<ReportViewProps> = ({
  platform = "meesho",
  report,
  availableMonths = [],
  selectedMonth = "",
  onMonthChange,
  onEditPrices,
  onReset,
  readOnly = false
}) => {
  const {
    orderStatuses: ORDER_STATUSES,
    skuTab,
    setSkuTab,
    orderTab,
    setOrderTab,
    skuSearch,
    setSkuSearch,
    orderSearch,
    setOrderSearch,
    showDetailedLedger,
    setShowDetailedLedger,
    skuPage,
    setSkuPage,
    skuRowsPerPage,
    setSkuRowsPerPage,
    orderPage,
    setOrderPage,
    rowsPerPage,
    setRowsPerPage,
    selectedSkuOrders,
    setSelectedSkuOrders,
    selectedSkuName,
    breakdownOrder,
    setBreakdownOrder,
    amazonBreakdownOrder,
    setAmazonBreakdownOrder,
    filteredSkuReports,
    skuCategoryCounts,
    paginatedSkuReports,
    totalSkuPages,
    orderSummaryMetrics,
    paginatedOrders,
    totalOrderPages,
    viewSkuDetails: handleViewSkuDetails
  } = useReportViewState(report, platform)

  const downloadSkuCsv = () => downloadSkuReport(report.skuReports, report.reportId)
  const downloadOrdersCsv = () => downloadOrderReport(report.orders, report.reportId)

  const netProfitColor = report.netProfit >= 0 ? "#22c55e" : "#ef4444"

  return (
    <Stack spacing={4}>
      {/* Report Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "flex-start" }}
        justifyContent="space-between"
        spacing={2}>
        <Stack spacing={0.5}>
          {!readOnly && availableMonths.length > 1 ? (
            <Box sx={{ minWidth: 200, display: "flex", alignItems: "center" }}>
              <Select
                value={selectedMonth}
                onChange={e => onMonthChange?.(e.target.value as string)}
                variant="outlined"
                size="small"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.35rem", sm: "1.65rem", md: "1.85rem" },
                  borderRadius: 2,
                  textTransform: "uppercase",
                  backgroundColor: "action.hover",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider"
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main"
                  },
                  color: "text.primary"
                }}>
                {availableMonths.map(m => (
                  <MenuItem key={m} value={m} sx={{ textTransform: "uppercase", fontWeight: 700 }}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ) : (
            <Typography
              variant="h4"
              fontWeight={800}
              letterSpacing="-0.02em"
              sx={{
                textTransform: "uppercase",
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" }
              }}>
              {report.periodLabel}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {platform === "amazon" ? "Amazon" : "Meesho"} Settlement Analysis Report
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.5} flexWrap="wrap">
          {!readOnly && onEditPrices && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={onEditPrices}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
              Edit SKU Prices
            </Button>
          )}
          {!readOnly && onReset && (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<RefreshIcon />}
              onClick={onReset}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
              Upload New
            </Button>
          )}
          <Chip
            label={`#${report.reportId}`}
            variant="outlined"
            size="small"
            sx={{
              fontWeight: 600,
              fontFamily: "monospace",
              fontSize: 12,
              borderRadius: 2,
              height: 32,
              px: 0.5,
              color: "text.secondary",
              borderColor: "divider"
            }}
          />
        </Stack>
      </Stack>

      {/* Grid: Profit Summary (Left) & Overall Statuses (Right) */}
      <Grid container spacing={3}>
        {/* PROFIT SUMMARY Card */}
        <Grid item xs={12} md={5}>
          <Card
            variant="outlined"
            sx={{
              height: "100%",
              borderRadius: 4,
              borderColor: `${netProfitColor}40`,
              background: theme =>
                theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${netProfitColor}12 0%, transparent 100%)`
                  : `linear-gradient(135deg, ${netProfitColor}06 0%, transparent 100%)`
            }}>
            <CardContent
              sx={{
                p: 3.5,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between"
              }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: "0.05em" }}>
                PROFIT SUMMARY
              </Typography>

              <Stack spacing={2.5} sx={{ my: 3 }}>
                {platform === "amazon" ? (
                  <>
                    {(() => {
                      const meta = (report as any).amazonMetadata || {
                        salesIncGst: 0,
                        refundsFees: 0,
                        netSettlement: 0,
                        advertising: 0,
                        cogs: 0,
                        netProfit: 0
                      }
                      const rawMeta = (report as any).rawAmazonMetadata || {
                        revenue: 0,
                        gstCollected: 0,
                        orderFees: 0,
                        shippingLabels: 0,
                        taxWithheld: 0
                      }
                      const feesPct =
                        meta.salesIncGst > 0 ? ((meta.refundsFees / meta.salesIncGst) * 100).toFixed(2) : "0.00"
                      const payoutPct =
                        meta.salesIncGst > 0 ? ((meta.netSettlement / meta.salesIncGst) * 100).toFixed(2) : "0.00"

                      return (
                        <>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              Sales (Inc GST)
                            </Typography>
                            <Typography variant="body1" fontWeight={700} color="success.main">
                              {fmt(meta.salesIncGst)}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              Refunds, Cancellations, and Fees (Inc GST)
                            </Typography>
                            <Typography variant="body1" fontWeight={700} color="error.main">
                              −{fmt(meta.refundsFees)} ({feesPct}%)
                            </Typography>
                          </Stack>
                          <Divider sx={{ borderStyle: "solid", my: 0.5 }} />
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" fontWeight={700} color="primary.main">
                              Net Settlement Received (Inc GST)
                            </Typography>
                            <Typography variant="body1" fontWeight={800} color="primary.main">
                              {fmt(meta.netSettlement)} ({payoutPct}%)
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              Other Charges (Cost of Advertising)
                            </Typography>
                            <Typography variant="body1" fontWeight={700} color="error.main">
                              −{fmt(meta.advertising)}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              Purchase Cost (COGS)
                            </Typography>
                            <Typography variant="body1" fontWeight={700} color="error.main">
                              −{fmt(meta.cogs)}
                            </Typography>
                          </Stack>

                          <Box sx={{ mt: 1.5 }}>
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => setShowDetailedLedger(!showDetailedLedger)}
                              sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                p: 0
                              }}>
                              {showDetailedLedger ? "Hide Detailed Ledger" : "Show Detailed Ledger"}
                            </Button>
                            <Collapse in={showDetailedLedger}>
                              <Stack
                                spacing={1}
                                sx={{
                                  mt: 1.5,
                                  pl: 1.5,
                                  borderLeft: "2px solid",
                                  borderColor: "divider"
                                }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={700}
                                  sx={{ letterSpacing: "0.05em", mb: 0.5 }}>
                                  DETAILED LEDGER BREAKDOWN
                                </Typography>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="caption" color="text.secondary">
                                    Gross Product Revenue
                                  </Typography>
                                  <Typography variant="caption" fontWeight={600} color="success.main">
                                    {fmt(rawMeta.revenue || report.totalSettlement + Math.abs(report.totalAdsCost))}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="caption" color="text.secondary">
                                    GST Tax Collected
                                  </Typography>
                                  <Typography variant="caption" fontWeight={600} color="success.main">
                                    +{fmt(rawMeta.gstCollected || 0)}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="caption" color="text.secondary">
                                    Amazon Order/selling Fees
                                  </Typography>
                                  <Typography variant="caption" fontWeight={600} color="error.main">
                                    {fmt(rawMeta.orderFees || 0, true)}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="caption" color="text.secondary">
                                    Shipping Services
                                  </Typography>
                                  <Typography variant="caption" fontWeight={600} color="error.main">
                                    {fmt(rawMeta.shippingLabels || 0, true)}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="caption" color="text.secondary">
                                    TCS/TDS Withheld
                                  </Typography>
                                  <Typography variant="caption" fontWeight={600} color="error.main">
                                    {fmt(rawMeta.taxWithheld || 0, true)}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Collapse>
                          </Box>
                        </>
                      )
                    })()}
                  </>
                ) : (
                  <>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Total Settlement
                      </Typography>
                      <Typography variant="body1" fontWeight={700}>
                        {fmt(report.totalSettlement)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Purchase Cost (COGS)
                      </Typography>
                      <Typography variant="body1" fontWeight={700} color="error.main">
                        −{fmt(report.totalPurchaseCost)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Ad Spend
                      </Typography>
                      <Typography variant="body1" fontWeight={700} color="error.main">
                        −{fmt(report.totalAdsCost)}
                      </Typography>
                    </Stack>
                    {report.totalReferral > 0 && (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          Referral Rewards
                        </Typography>
                        <Typography variant="body1" fontWeight={700} color="success.main">
                          +{fmt(report.totalReferral)}
                        </Typography>
                      </Stack>
                    )}
                  </>
                )}
              </Stack>

              <Divider sx={{ borderStyle: "dashed" }} />

              <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mt: 3.5 }}>
                <Stack spacing={0.5}>
                  <Typography
                    variant="h4"
                    fontWeight={850}
                    sx={{
                      color: netProfitColor,
                      letterSpacing: "-0.03em",
                      fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" }
                    }}>
                    {report.netProfit < 0 ? "−" : ""}
                    {fmt(report.netProfit)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Net Profit (GST land costs included)
                  </Typography>
                </Stack>
                <Chip
                  label={report.netProfit >= 0 ? "You are in Profit" : "You are in Loss"}
                  sx={{
                    fontWeight: 800,
                    bgcolor: report.netProfit >= 0 ? "#e2fbe8" : "#ffebee",
                    color: report.netProfit >= 0 ? "#1b5e20" : "#c62828"
                  }}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* OVERALL ORDERS Statuses splits */}
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: 3.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: "0.05em" }}>
                  OVERALL ORDERS
                </Typography>
                <Typography variant="body2" fontWeight={700} color="primary.main">
                  {report.totalOrders} Orders
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                {/* Delivered column split */}
                <Grid item xs={12} sm={platform === "amazon" ? 4 : 6}>
                  <Stack
                    spacing={2}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: "rgba(34, 197, 94, 0.05)",
                      border: "1px solid rgba(34, 197, 94, 0.15)",
                      height: "100%"
                    }}>
                    <Typography variant="body2" fontWeight={700} color="success.main" display="block">
                      Delivered ({report.deliveredSplit.notReturned.count + report.deliveredSplit.returned.count}{" "}
                      Orders)
                    </Typography>
                    <Divider />
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Not Returned
                        </Typography>
                        <Stack alignItems="right" textAlign="right">
                          <Typography variant="body2" fontWeight={600}>
                            {report.deliveredSplit.notReturned.count} orders
                          </Typography>
                          {platform !== "amazon" && (
                            <Typography variant="caption" color="text.secondary">
                              {fmt(report.deliveredSplit.notReturned.settlement)}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Returned
                        </Typography>
                        <Stack alignItems="right" textAlign="right">
                          <Typography variant="body2" fontWeight={600} color="warning.main">
                            {report.deliveredSplit.returned.count} orders
                          </Typography>
                          {platform !== "amazon" && (
                            <Typography variant="caption" color="text.secondary">
                              {fmt(report.deliveredSplit.returned.settlement)}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </Grid>

                {/* Un-Delivered column split */}
                <Grid item xs={12} sm={platform === "amazon" ? 4 : 6}>
                  <Stack
                    spacing={2}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: "rgba(239, 68, 68, 0.05)",
                      border: "1px solid rgba(239, 68, 68, 0.15)",
                      height: "100%"
                    }}>
                    <Typography variant="body2" fontWeight={700} color="error.main" display="block">
                      Un-Delivered (
                      {platform === "amazon"
                        ? report.undeliveredSplit.rtoComplete.count
                        : report.totalOrders -
                          (report.deliveredSplit.notReturned.count + report.deliveredSplit.returned.count)}{" "}
                      Orders)
                    </Typography>
                    <Divider />
                    <Stack spacing={1.2}>
                      {platform === "amazon" ? (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">
                            COURIER RETURN
                          </Typography>
                          <Typography variant="caption" fontWeight={600}>
                            {report.undeliveredSplit.rtoComplete.count} orders
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">
                            CANCELLED
                          </Typography>
                          <Typography variant="caption" fontWeight={600}>
                            {report.undeliveredSplit.cancelled.count} orders
                          </Typography>
                        </Stack>
                      )}
                      {platform !== "amazon" && (
                        <>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                              CLAIMED
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                              {report.undeliveredSplit.claimed.count} orders
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                              RTO COMPLETE
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                              {report.undeliveredSplit.rtoComplete.count} orders
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                              EXCHANGED
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                              {report.undeliveredSplit.exchanged.count} orders
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                              RTO LOCKED
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                              {report.undeliveredSplit.rtoLocked.count} orders
                            </Typography>
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Grid>

                {/* Amazon Easy Ship column split */}
                {platform === "amazon" && (
                  <Grid item xs={12} sm={4}>
                    <Stack
                      spacing={2}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: "rgba(59, 130, 246, 0.05)",
                        border: "1px solid rgba(59, 130, 246, 0.15)",
                        height: "100%"
                      }}>
                      <Typography variant="body2" fontWeight={700} color="primary.main" display="block">
                        Easy Ship ({(report as any).amazonMetadata?.easyShipOrders || 0} Orders)
                      </Typography>
                      <Divider />
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Easy Ship Shipments
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {(report as any).amazonMetadata?.easyShipOrders || 0} orders
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SKU WISE PROFITS SECTION */}
      <Card variant="outlined" sx={{ borderRadius: 4 }}>
        <CardContent sx={{ p: 3.5 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            spacing={2}
            sx={{ mb: 3 }}>
            <Stack spacing={0.5}>
              <Typography variant="h6" fontWeight={700}>
                SKU Wise Profits
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Analyze and sort SKU financial performance and margins
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} width={{ xs: "100%", sm: "auto" }}>
              <TextField
                size="small"
                placeholder="Search SKU..."
                value={skuSearch}
                onChange={e => {
                  setSkuSearch(e.target.value)
                  setSkuPage(1)
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  width: 180,
                  "& .MuiOutlinedInput-root": { borderRadius: 2 }
                }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={downloadSkuCsv}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
                DOWNLOAD CSV
              </Button>
            </Stack>
          </Stack>

          {/* SKU tabs */}
          <Tabs
            value={skuTab}
            onChange={(_, val) => {
              setSkuTab(val)
              setSkuPage(1)
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
            <Tab label={`ALL (${skuCategoryCounts.ALL})`} />
            <Tab label={`HIGH PROFIT (${skuCategoryCounts.HIGH})`} />
            <Tab label={`MEDIUM PROFIT (${skuCategoryCounts.MEDIUM})`} />
            <Tab label={`LOW PROFIT (${skuCategoryCounts.LOW})`} />
            <Tab label={`LOSS MAKING (${skuCategoryCounts.LOSS})`} />
          </Tabs>

          {/* SKU Table */}
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              borderRadius: 3,
              border: "none",
              overflowX: "auto",
              width: "100%",
              display: "block"
            }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "background.neutral" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Product Name / SKU</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Settlement
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Purchase Cost
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Sold/Returned
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Net Profit
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, width: 80 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedSkuReports.map((item, idx) => (
                  <TableRow hover key={idx}>
                    <TableCell>
                      <Tooltip title={item.productName} placement="top">
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 300, cursor: "pointer" }}>
                          {item.productName}
                        </Typography>
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                        SKU: {item.sku}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{fmt(item.settlement)}</TableCell>
                    <TableCell align="right">{fmt(item.purchaseCost)}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600}>
                        {item.unitsSold} /{" "}
                        <Typography component="span" variant="body2" color="warning.main" fontWeight={600}>
                          {item.unitsReturned}
                        </Typography>
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color={item.profit >= 0 ? "success.main" : "error.main"}>
                        {item.profit >= 0 ? "profit" : "loss"} {fmt(item.profit)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewSkuDetails(item.sku, item.productName)}
                        sx={{
                          borderRadius: 1.5,
                          textTransform: "none",
                          py: 0.25
                        }}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}

                {filteredSkuReports.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No SKUs found matching filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* SKU Table Pagination Controls */}
          {totalSkuPages > 1 && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ pt: 2, borderTop: 1, borderColor: "divider", mt: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Rows per page:
                </Typography>
                <Select
                  size="small"
                  value={skuRowsPerPage}
                  onChange={e => {
                    setSkuRowsPerPage(Number(e.target.value))
                    setSkuPage(1)
                  }}
                  sx={{
                    borderRadius: 1.5,
                    height: 30,
                    "& .MuiSelect-select": { py: 0.5 }
                  }}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </Stack>
              <Pagination
                count={totalSkuPages}
                page={skuPage}
                onChange={(_, p) => setSkuPage(p)}
                color="primary"
                size="small"
              />
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* ORDER WISE PROFITS SECTION */}
      <Card variant="outlined" sx={{ borderRadius: 4 }}>
        <CardContent sx={{ p: 3.5 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            spacing={2}
            sx={{ mb: 2 }}>
            <Stack spacing={0.5}>
              <Typography variant="h6" fontWeight={700}>
                Order Wise Profits
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Search, audit, and analyze individual order profit lines
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} width={{ xs: "100%", sm: "auto" }}>
              <TextField
                size="small"
                placeholder="SKU Search..."
                value={orderSearch}
                onChange={e => {
                  setOrderSearch(e.target.value)
                  setOrderPage(1)
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  width: 180,
                  "& .MuiOutlinedInput-root": { borderRadius: 2 }
                }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={downloadOrdersCsv}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
                DOWNLOAD CSV
              </Button>
            </Stack>
          </Stack>

          {/* Order tabs */}
          <Tabs
            value={orderTab}
            onChange={(_, val) => {
              setOrderTab(val)
              setOrderPage(1)
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
            {ORDER_STATUSES.map((st, i) => (
              <Tab key={i} label={st} />
            ))}
          </Tabs>

          {/* Order metrics overview for this search / category filter */}
          <Box sx={{ p: 2, mb: 2, bgcolor: "background.neutral", borderRadius: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Total Settlement
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {fmt(orderSummaryMetrics.settlement)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Total Orders
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {orderSummaryMetrics.count}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Total Net Profit
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  color={orderSummaryMetrics.profit >= 0 ? "success.main" : "error.main"}>
                  {fmt(orderSummaryMetrics.profit)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Avg Profit / Order
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  color={orderSummaryMetrics.avgProfit >= 0 ? "success.main" : "error.main"}>
                  {fmt(orderSummaryMetrics.avgProfit)}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Orders Table */}
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              borderRadius: 3,
              border: "none",
              overflowX: "auto",
              width: "100%",
              display: "block"
            }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "background.neutral" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Order Id</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Product Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Settlement
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Purchase
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Profit
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, width: 80 }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedOrders.map((ord, idx) => {
                  const stateStyles = getStatusColor(ord.liveOrderStatus)
                  return (
                    <TableRow hover key={idx}>
                      <TableCell sx={{ fontFamily: "monospace" }}>{ord.subOrderNo}</TableCell>
                      <TableCell>
                        <Tooltip title={ord.productName} placement="top">
                          <Typography variant="body2" noWrap sx={{ maxWidth: 220, cursor: "pointer" }}>
                            {ord.productName}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{ord.sku}</TableCell>
                      <TableCell>
                        <Chip
                          label={stateStyles.label}
                          size="small"
                          sx={{
                            bgcolor: stateStyles.bg,
                            color: stateStyles.color,
                            fontWeight: 700,
                            fontSize: 10,
                            borderRadius: 1
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">{fmt(ord.settlementAmount)}</TableCell>
                      <TableCell align="right">{fmt(ord.cogs)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={ord.profit >= 0 ? "success.main" : "error.main"}>
                          {fmt(ord.profit)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => {
                            if (platform === "amazon") {
                              setAmazonBreakdownOrder(ord)
                            } else {
                              setBreakdownOrder(ord)
                            }
                          }}
                          sx={{
                            borderRadius: 1.5,
                            textTransform: "none",
                            py: 0.25
                          }}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}

                {paginatedOrders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No orders found matching filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalOrderPages > 1 && (
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Rows per page:
                </Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={e => {
                    setRowsPerPage(Number(e.target.value))
                    setOrderPage(1)
                  }}
                  sx={{
                    borderRadius: 1.5,
                    height: 30,
                    "& .MuiSelect-select": { py: 0.5 }
                  }}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </Stack>
              <Pagination
                count={totalOrderPages}
                page={orderPage}
                onChange={(_, p) => setOrderPage(p)}
                color="primary"
                size="small"
              />
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* MODAL 1: SKU Orders Dialog */}
      <Dialog open={selectedSkuOrders !== null} onClose={() => setSelectedSkuOrders(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1, borderBottom: 1, borderColor: "divider" }}>
          <Stack spacing={0.5}>
            <Typography variant="h6" fontWeight={700}>
              Order Transactions for SKU
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 600 }}>
              {selectedSkuName}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 2, height: "450px", overflowY: "auto" }}>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              borderRadius: 3,
              border: "none",
              mt: 1,
              overflowX: "auto",
              width: "100%",
              display: "block"
            }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "background.neutral" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Order Id</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Order Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Settlement
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Purchase Cost
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Profit/Loss
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedSkuOrders?.map((o, idx) => {
                  const stateStyles = getStatusColor(o.liveOrderStatus)
                  return (
                    <TableRow hover key={idx}>
                      <TableCell sx={{ fontFamily: "monospace" }}>{o.subOrderNo}</TableCell>
                      <TableCell>{o.orderDate}</TableCell>
                      <TableCell>
                        <Chip
                          label={stateStyles.label}
                          size="small"
                          sx={{
                            bgcolor: stateStyles.bg,
                            color: stateStyles.color,
                            fontWeight: 700,
                            fontSize: 9,
                            borderRadius: 1
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">{fmt(o.settlementAmount)}</TableCell>
                      <TableCell align="right">{fmt(o.cogs)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={o.profit >= 0 ? "success.main" : "error.main"}>
                          {fmt(o.profit)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
          <Button onClick={() => setSelectedSkuOrders(null)} variant="contained" sx={{ borderRadius: 2 }}>
            Close Dialog
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL 2: 18 Component Ledger Breakdown Modal */}
      <OrderBreakdownModal
        open={breakdownOrder !== null}
        onClose={() => setBreakdownOrder(null)}
        order={breakdownOrder?.originalOrderRow}
        orderId={breakdownOrder?.subOrderNo || ""}
        sku={breakdownOrder?.sku || ""}
        productName={breakdownOrder?.productName || ""}
      />

      {/* MODAL 3: Amazon Raw Transaction Entries */}
      <Dialog
        open={amazonBreakdownOrder !== null}
        onClose={() => setAmazonBreakdownOrder(null)}
        maxWidth="md"
        fullWidth>
        <DialogTitle sx={{ pb: 1, borderBottom: 1, borderColor: "divider" }}>
          <Stack spacing={0.5}>
            <Typography variant="h6" fontWeight={700}>
              Transaction Ledger Entries
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Order ID: <code>{amazonBreakdownOrder?.subOrderNo}</code> | SKU: <code>{amazonBreakdownOrder?.sku}</code>
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 2, height: "400px", overflowY: "auto" }}>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, border: "none", mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "background.neutral" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Date/Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Qty
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Total Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {((amazonBreakdownOrder as any)?.originalRows || []).map((r: any, idx: number) => (
                  <TableRow hover key={idx}>
                    <TableCell>{r.dateTime}</TableCell>
                    <TableCell>
                      <Chip label={r.type} size="small" sx={{ fontSize: 10, fontWeight: 700 }} />
                    </TableCell>
                    <TableCell>{r.description || "N/A"}</TableCell>
                    <TableCell align="right">{r.quantity || 0}</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        color: r.total >= 0 ? "success.main" : "error.main"
                      }}>
                      {fmt(r.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
          <Button onClick={() => setAmazonBreakdownOrder(null)} variant="contained" sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default ReportView
